import { format } from 'date-fns';
import { useMemo } from 'react';
import { useAuthState } from 'state';
import { PaymentDetail } from 'types';
import ApiService from './ApiService';
import { HttpStatusCode } from 'axios';

const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

export class DeductionService extends ApiService {
    
    async getDeductionDocuments(accountId: string, vendorId: string, paymentDetail: PaymentDetail) {
        const { data, status } = await this.get(`/api/accounts/${accountId}/vendors/${vendorId}/deductions/${paymentDetail.invoiceId}/documents`
            + this.getOptionalQueryParam(true, 'AdjustmentTypeId', paymentDetail.adjustmentTypeId?.toString())
            + this.getOptionalQueryParam(false, 'AdjustmentAmount', paymentDetail.adjustmentAmount?.toString() ?? "0")
            + this.getOptionalQueryParam(false, 'InvoiceId', paymentDetail.invoiceId)
            + this.getOptionalQueryParam(false, 'MockInvoice', paymentDetail.mockInvoice)
            + this.getOptionalQueryParam(false, 'CustomerId', paymentDetail.customerId)
            + this.getOptionalQueryParam(false, 'CustomerItemReference', paymentDetail.customerItemReference)
            + this.getOptionalQueryParam(false, 'SupplierId', paymentDetail.onBaseSupplierId)
            + this.getOptionalQueryParam(false, 'DeductAmt', paymentDetail.adjustmentAmount.toString()));
        return status === HttpStatusCode.Ok ? data : [];
    }

    async getDeductionDetails(accountId: string, vendorId: string, invoiceId: string, referenceId?: string): Promise<PaymentDetail> {
        const { data } =
            await this.get(`/api/accounts/${accountId}/vendors/${vendorId}/deductions/${invoiceId}`
                + this.getOptionalQueryParam(true, 'ReferenceId', referenceId));
        return data;
    }

    // The getDeductionsByDate endpoint returns payment details by date range. The business refers
    // to this endpoint as "deductions", but this refers to Dot's deductions (i.e., adjustments) on 
    // payments to suppliers. We are reusing the PaymentDetail type and payment detail components 
    // for this endpoint.  
    async getDeductionsByDate(accountId: string, startDate: Date, endDate: Date, customerId?: string): Promise<PaymentDetail[]> {
        const { data, status } =
            await this.get(`/api/accounts/${accountId}/deductions?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`
                + this.getOptionalQueryParam(false, 'customerId', customerId));
        return this.IsSuccessStatusCode(status) ? data : [];
    }
}

export const useDeductionService = () => {
    const auth = useAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => new DeductionService(), [auth]);
};